import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { vocabularyApi, languagesApi, lexiconApi } from '../utils/api';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { getWordColor, formatStrength, getStatusColorClass } from '../utils/strengthUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Search, 
  Filter, 
  BookMarked,
  StickyNote,
  RefreshCw,
  Eye,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Brain,
  Layers,
  Plus,
  Trash2,
  ArrowRightLeft,
  Copy,
} from 'lucide-react';
import MemoryAwareness from '../components/MemoryAwareness';
import { useWordPopover } from '../shared/reader/useWordPopover';
import WordPopover from '../shared/reader/WordPopover';
import { WordDetailSheet } from '../components/vocabulary/WordDetailSheet';

const ITEMS_PER_PAGE = 30;

export default function Vocabulary() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [languages, setLanguages] = useState([]);
  const [vocabulary, setVocabulary] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedWord, setSelectedWord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Full-screen word detail sheet (#35)
  const [detailWord, setDetailWord] = useState(null);
  const [detailLangCode, setDetailLangCode] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    language_id: '',
    status: '',
    search: '',
    offset: 0
  });

  // Derive current language code for popover
  const currentLangCode = languages.find(l => l.language_id === filters.language_id)?.code || '';
  const popover = useWordPopover({ languageCode: currentLangCode });

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        // Get only languages the user has vocabulary in
        const res = await vocabularyApi.getLanguages();
        setLanguages(res.data);
      } catch (err) {
        console.error('Error fetching languages:', err);
      }
    };

    fetchLanguages();
  }, []);

  useEffect(() => {
    const fetchVocabulary = async () => {
      setLoading(true);
      try {
        const params = {
          limit: ITEMS_PER_PAGE,
          offset: filters.offset
        };
        
        if (filters.language_id) params.language_id = filters.language_id;
        if (filters.status) params.status = filters.status;
        if (filters.search) params.search = filters.search;

        const [vocabRes, countRes] = await Promise.all([
          vocabularyApi.getAll(params),
          vocabularyApi.getCount({ language_id: filters.language_id })
        ]);
        
        setVocabulary(vocabRes.data);
        setCounts(countRes.data);
      } catch (err) {
        console.error('Error fetching vocabulary:', err);
        toast.error('Failed to load vocabulary');
      } finally {
        setLoading(false);
      }
    };

    fetchVocabulary();
  }, [filters]);

  const handleWordClick = (word, event) => {
    const wordText = word?.lexeme?.normalized_form || word?.normalized_form || '';
    // Resolve language code from the word or current filter
    const lang = languages.find(l => l.language_id === word?.language_id);
    const langCode = word?.language_code || lang?.code || currentLangCode;
    if (wordText && langCode) {
      const token = {
        token_id: `vocab-${word.state_id}`,
        normalized_form: wordText.toLowerCase(),
        surface_form: wordText,
        language_code: langCode,
        is_word: true,
      };
      popover.open(token, event);
    } else {
      vocabularyApi.getById(word.state_id).then(res => {
        setSelectedWord(res.data);
        setDialogOpen(true);
      }).catch(err => console.error('Error fetching word details:', err));
    }
  };

  // ── Popover action handlers ──
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [actionLoading, setActionLoading] = useState(false);

  const popoverLang = () => {
    if (popover.selectedToken?.language_code) return popover.selectedToken.language_code;
    const term = popover.selectedToken?.normalized_form;
    const word = vocabulary.find(w => (w.lexeme?.normalized_form || '').toLowerCase() === term);
    const lang = languages.find(l => l.language_id === word?.language_id);
    return lang?.code || currentLangCode;
  };

  const handleSetStatus = async (status) => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    const lc = popoverLang();
    setActionLoading(true);
    try {
      await vocabularyApi.setWordStatus(term, lc, status);
      toast.success(`Marked as ${status}`);
      popover.close();
    } catch { toast.error('Could not update status'); }
    finally { setActionLoading(false); }
  };

  const handleSaveTranslation = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    const lc = popoverLang();
    setActionLoading(true);
    try {
      let stateId = popover.wordData?.user_state?.state_id;
      if (!stateId) {
        await vocabularyApi.setWordStatus(term, lc, 'new');
        const res = await vocabularyApi.getWordData(term, lc);
        stateId = res.data?.user_state?.state_id;
      }
      if (stateId) {
        await vocabularyApi.addTranslation(stateId, popover.translation);
        popover.setTranslationSaved(true);
        toast.success('Translation saved');
      }
    } catch { toast.error('Could not save translation'); }
    finally { setActionLoading(false); }
  };

  const handleSaveNote = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    const lc = popoverLang();
    setActionLoading(true);
    try {
      await vocabularyApi.saveUserNote(term, lc, popover.note);
      toast.success('Note saved');
    } catch { toast.error('Could not save note'); }
    finally { setActionLoading(false); }
  };

  const handleAutoTranslate = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    const lc = popoverLang();
    setActionLoading(true);
    try {
      const res = await vocabularyApi.enrichWord(term, lc, 'translation', true);
      const tr = res.data?.data?.translation || res.data?.translation;
      if (tr) { popover.setTranslation(tr); popover.setTranslationSaved(false); }
    } catch { toast.error('Translation failed'); }
    finally { setActionLoading(false); }
  };

  const handleRefreshSection = async (section) => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    const lc = popoverLang();
    setActionLoading(true);
    try {
      const res = await vocabularyApi.enrichWord(term, lc, section, true);
      if (res.data?.data) popover.setWordData(prev => prev ? { ...prev, ...res.data.data } : res.data.data);
      toast.success(section ? `${section} refreshed` : 'Word enriched');
    } catch { toast.error('Refresh failed'); }
    finally { setActionLoading(false); }
  };

  const handleToggleFieldLock = async (field) => {
    const term = popover.selectedToken?.normalized_form || popover.wordData?.word;
    const lc = popoverLang();
    if (!term || !lc) return;
    setActionLoading(true);
    try {
      const res = await lexiconApi.toggleFieldLock(term, lc, field);
      const newLocked = res.data?.locked_fields || [];
      popover.setWordData(prev => prev ? { ...prev, locked_fields: newLocked } : prev);
      toast.success(`${field} ${res.data?.action || 'toggled'}`);
    } catch { toast.error('Failed to update field lock'); }
    finally { setActionLoading(false); }
  };

  const handlePlayAudio = async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    const lc = popoverLang();
    try {
      const audio = new Audio(`${BACKEND_URL}/api/tts/word?word=${encodeURIComponent(term)}&language=${lc}`);
      await audio.play();
    } catch { toast.error('Audio unavailable'); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already triggered by the useEffect when filters change
  };

  const handleNextPage = () => {
    setFilters(prev => ({ ...prev, offset: prev.offset + ITEMS_PER_PAGE }));
  };

  const handlePrevPage = () => {
    setFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - ITEMS_PER_PAGE) }));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('vocabulary.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('vocabulary.subtitle') || 'Browse and manage your learned words'}
          </p>
        </div>
      </div>

      <Tabs defaultValue="words" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="words" className="flex items-center gap-2">
            <BookMarked className="h-4 w-4" />
            Word List
          </TabsTrigger>
          <TabsTrigger value="flashcards" className="flex items-center gap-2" data-testid="flashcards-tab">
            <Layers className="h-4 w-4" />
            Flashcards
          </TabsTrigger>
          <TabsTrigger value="memory" className="flex items-center gap-2" data-testid="memory-tab">
            <Brain className="h-4 w-4" />
            Memory Awareness
          </TabsTrigger>
        </TabsList>

        <TabsContent value="memory">
          <MemoryAwareness />
        </TabsContent>

        <TabsContent value="flashcards">
          <FlashcardManager languageCode={currentLangCode} />
        </TabsContent>

        <TabsContent value="words">

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatusBadge 
          label={t('dashboard.totalWords') || 'Total'} 
          count={counts.total || 0} 
          active={!filters.status}
          onClick={() => setFilters(prev => ({ ...prev, status: '', offset: 0 }))}
        />
        <StatusBadge 
          label={t('vocabulary.new')} 
          count={counts.new || 0} 
          color="text-red-500"
          active={filters.status === 'new'}
          onClick={() => setFilters(prev => ({ ...prev, status: 'new', offset: 0 }))}
        />
        <StatusBadge 
          label={t('vocabulary.learning')} 
          count={counts.learning || 0}
          color="text-purple-500"
          active={filters.status === 'learning'}
          onClick={() => setFilters(prev => ({ ...prev, status: 'learning', offset: 0 }))}
        />
        <StatusBadge 
          label={t('vocabulary.known')} 
          count={counts.known || 0}
          color="text-purple-400"
          active={filters.status === 'known'}
          onClick={() => setFilters(prev => ({ ...prev, status: 'known', offset: 0 }))}
        />
        <StatusBadge 
          label={t('vocabulary.mastered')} 
          count={counts.mastered || 0}
          color="text-blue-500"
          active={filters.status === 'mastered'}
          onClick={() => setFilters(prev => ({ ...prev, status: 'mastered', offset: 0 }))}
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('vocabulary.search')}
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, offset: 0 }))}
                data-testid="search-input"
              />
            </div>
            <Select
              value={filters.language_id || "all"}
              onValueChange={(value) => setFilters(prev => ({ ...prev, language_id: value === "all" ? "" : value, offset: 0 }))}
            >
              <SelectTrigger className="w-full md:w-48" data-testid="language-filter">
                <SelectValue placeholder={t('vocabulary.allLanguages')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('vocabulary.allLanguages')}</SelectItem>
                {languages.map(lang => (
                  <SelectItem key={lang.language_id} value={lang.language_id}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(filters.search || filters.language_id || filters.status) && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setFilters({ language_id: '', status: '', search: '', offset: 0 })}
                data-testid="clear-filters-btn"
              >
                <X className="h-4 w-4 mr-2" />
                {t('common.clear') || 'Clear'}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Vocabulary List */}
      <Card data-testid="vocabulary-list-card">
        <CardContent className="p-0">
          {loading ? (
            <VocabularySkeleton />
          ) : vocabulary.length === 0 ? (
            <div className="text-center py-12">
              <BookMarked className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">
                {filters.search || filters.status || filters.language_id
                  ? 'No words match your filters'
                  : 'No vocabulary yet. Start reading to build your word bank!'}
              </p>
            </div>
          ) : (
            <>
              <ScrollArea className="h-[500px]">
                <div className="divide-y">
                  {vocabulary.map(word => (
                    <VocabularyItem
                      key={word.state_id}
                      word={word}
                      onClick={(e) => handleWordClick(word, e)}
                      onExpand={() => {
                        const wordText = word?.lexeme?.normalized_form || word?.normalized_form || '';
                        const lang = languages.find(l => l.language_id === word?.language_id);
                        const langCode = word?.language_code || lang?.code || currentLangCode;
                        setDetailWord(wordText);
                        setDetailLangCode(langCode);
                        setDetailOpen(true);
                      }}
                    />
                  ))}
                </div>
              </ScrollArea>
              
              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={filters.offset === 0}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Showing {filters.offset + 1} - {Math.min(filters.offset + ITEMS_PER_PAGE, counts.total || 0)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={filters.offset + ITEMS_PER_PAGE >= (counts.total || 0)}
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Word Detail Dialog */}
      <WordDetailDialog
        word={selectedWord}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      {/* Universal Word Popover */}
      {popover.popoverPos && (
        <WordPopover
          style={popover.popoverPos}
          wordData={popover.wordData}
          wordLoading={popover.wordLoading}
          token={popover.selectedToken}
          language={{ code: popoverLang() }}
          noteText={popover.note}
          setNoteText={popover.setNote}
          translationText={popover.translation}
          setTranslationText={popover.setTranslation}
          translationSaved={popover.translationSaved}
          onPlayAudio={handlePlayAudio}
          onSetStatus={handleSetStatus}
          onRelatedWordClick={(term) => popover.openForTerm(term)}
          onPopoverBack={() => popover.back()}
          historyDepth={popover.historyDepth}
          onSaveTranslation={handleSaveTranslation}
          onAutoTranslate={handleAutoTranslate}
          onSaveNote={handleSaveNote}
          onRefreshSection={handleRefreshSection}
          onToggleFieldLock={handleToggleFieldLock}
          onClose={() => popover.close()}
          actionLoading={actionLoading}
          locked={popover.wordData?.locked}
          lockedFields={popover.wordData?.locked_fields || []}
          isMobile={popover.popoverPos?.isMobile}
          isAdmin={user?.is_admin}
          isExpression={popover.isExpression}
          hasCachedAudio={popover.hasCachedAudio}
          isNewEntry={popover.isNewEntry}
        />
      )}

        </TabsContent>
      </Tabs>

      {/* Full-screen word detail sheet (#35) */}
      <WordDetailSheet
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        word={detailWord}
        languageCode={detailLangCode}
      />
    </div>
  );
}

function StatusBadge({ label, count, color = 'text-foreground', active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-lg border text-left transition-colors ${
        active ? 'bg-accent/10 border-accent' : 'hover:bg-muted/50'
      }`}
      data-testid={`status-badge-${label.toLowerCase()}`}
    >
      <p className={`text-xl font-bold ${color}`}>{count}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </button>
  );
}

function VocabularyItem({ word, onClick, onExpand }) {
  const strength = word?.strength || 0;
  const color = getWordColor(strength);

  return (
    <button
      onClick={onClick}
      onDoubleClick={(e) => { e.preventDefault(); onExpand?.(); }}
      className="w-full p-4 text-left hover:bg-muted/50 transition-colors"
      data-testid={`vocab-item-${word.state_id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div 
            className="w-2 h-8 rounded-full" 
            style={{ backgroundColor: color }}
          />
          <div>
            <p className="font-medium font-reader text-lg" style={{ color }}>
              {word?.lexeme?.normalized_form}
            </p>
            {word?.note && (
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <StickyNote className="h-3 w-3" />
                {word.note.slice(0, 50)}{word.note.length > 50 ? '...' : ''}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium" style={{ color }}>
              {formatStrength(strength)}
            </p>
            <p className="text-xs text-muted-foreground">
              {word?.exposure_count || 0} exp · {word?.review_count || 0} rev
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={getStatusColorClass(word?.status)}>
              {word?.status}
            </Badge>
            <Eye
              className="h-4 w-4 text-muted-foreground/40 hover:text-primary cursor-pointer shrink-0"
              onClick={(e) => { e.stopPropagation(); onExpand?.(); }}
              data-testid={`expand-word-${word.state_id}`}
            />
          </div>
        </div>
      </div>
    </button>
  );
}

function WordDetailDialog({ word, open, onOpenChange }) {
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (word) {
      setNoteText(word.note?.note_text || word.note || '');
    }
  }, [word]);

  const handleSaveNote = async () => {
    if (!word?.state_id) return;
    setSaving(true);
    
    try {
      await vocabularyApi.addNote(word.state_id, noteText);
      toast.success('Note saved');
    } catch (err) {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  if (!word) return null;

  const strength = word?.strength || 0;
  const color = getWordColor(strength);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="word-detail-dialog">
        <DialogHeader>
          <DialogTitle 
            className="text-2xl font-reader"
            style={{ color }}
          >
            {word?.lexeme?.normalized_form}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Lexeme Info */}
          <div className="flex flex-wrap gap-2">
            {word?.lexeme?.lemma && (
              <Badge variant="outline">Lemma: {word.lexeme.lemma}</Badge>
            )}
            {word?.lexeme?.part_of_speech && (
              <Badge variant="secondary">{word.lexeme.part_of_speech}</Badge>
            )}
            <Badge className={getStatusColorClass(word?.status)}>
              {word?.status}
            </Badge>
          </div>

          {/* Strength */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Strength</span>
              <span style={{ color }}>{formatStrength(strength)}</span>
            </div>
            <Progress value={strength * 100} className="h-2" />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <span>{word?.exposure_count || 0} exposures</span>
            </div>
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
              <span>{word?.review_count || 0} reviews</span>
            </div>
          </div>

          {/* Gloss */}
          {word?.lexeme?.gloss && (
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground mb-1">Definition</p>
              <p>{word.lexeme.gloss}</p>
            </div>
          )}

          {/* Note */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Personal Note</p>
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note..."
              className="min-h-[80px]"
              data-testid="word-note-input"
            />
            <Button
              size="sm"
              onClick={handleSaveNote}
              disabled={saving}
              className="w-full"
              data-testid="save-word-note-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Note'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VocabularySkeleton() {
  return (
    <div className="divide-y">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Skeleton className="w-2 h-8 rounded-full" />
              <Skeleton className="h-6 w-32" />
            </div>
            <Skeleton className="h-6 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}


function FlashcardManager({ languageCode }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState(null);
  const [listWords, setListWords] = useState([]);
  const [loadingWords, setLoadingWords] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchLists = async () => {
    try {
      setLoading(true);
      const res = await vocabularyApi.getFlashcardLists();
      setLists(res.data || []);
    } catch { toast.error('Failed to load flashcard lists'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLists(); }, []);

  const handleCreate = async () => {
    if (!newListName.trim()) return;
    setCreating(true);
    try {
      await vocabularyApi.createFlashcardList({ name: newListName.trim(), language_code: languageCode });
      setNewListName('');
      fetchLists();
      toast.success('List created');
    } catch { toast.error('Failed to create list'); }
    finally { setCreating(false); }
  };

  const handleOpenList = async (list) => {
    setSelectedList(list);
    setLoadingWords(true);
    try {
      const res = await vocabularyApi.getFlashcardListWords(list.list_id);
      setListWords(res.data?.words || res.data || []);
    } catch { toast.error('Failed to load words'); }
    finally { setLoadingWords(false); }
  };

  const handleDeleteList = async (listId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this list? Words will remain in your vocabulary.')) return;
    try {
      await vocabularyApi.deleteFlashcardList(listId);
      if (selectedList?.list_id === listId) { setSelectedList(null); setListWords([]); }
      fetchLists();
      toast.success('List deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleRemoveWord = async (wordId) => {
    if (!selectedList) return;
    try {
      await vocabularyApi.removeFromFlashcardList(selectedList.list_id, wordId);
      setListWords(prev => prev.filter(w => (w.word_id || w._id) !== wordId));
      toast.success('Word removed from list');
    } catch { toast.error('Failed to remove word'); }
  };

  const handleCopyWord = async (wordId, targetListId) => {
    if (!selectedList) return;
    try {
      await vocabularyApi.copyFlashcardWord(selectedList.list_id, wordId, targetListId);
      toast.success('Word copied');
    } catch { toast.error('Failed to copy word'); }
  };

  const handleMoveWord = async (wordId, targetListId) => {
    if (!selectedList) return;
    try {
      await vocabularyApi.moveFlashcardWord(selectedList.list_id, wordId, targetListId);
      setListWords(prev => prev.filter(w => (w.word_id || w._id) !== wordId));
      toast.success('Word moved');
    } catch { toast.error('Failed to move word'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="flashcard-manager">
      {/* Create new list */}
      <div className="flex gap-2">
        <Input
          placeholder="New list name..."
          value={newListName}
          onChange={(e) => setNewListName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          className="max-w-xs"
          data-testid="new-list-input"
        />
        <Button onClick={handleCreate} disabled={creating || !newListName.trim()} size="sm" data-testid="create-list-btn">
          <Plus className="h-4 w-4 mr-1" /> Create
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Lists panel */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">Your Lists ({lists.length})</h3>
          {lists.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No flashcard lists yet. Create one above.</p>
          ) : (
            <div className="space-y-1">
              {lists.map(list => (
                <button
                  key={list.list_id}
                  onClick={() => handleOpenList(list)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between group ${
                    selectedList?.list_id === list.list_id ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'
                  }`}
                  data-testid={`flashcard-list-${list.list_id}`}
                >
                  <div>
                    <span className="font-medium">{list.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">{list.word_count || 0} words</span>
                  </div>
                  <Trash2
                    className="h-3.5 w-3.5 text-muted-foreground/40 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => handleDeleteList(list.list_id, e)}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Words panel */}
        <div className="md:col-span-2">
          {selectedList ? (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{selectedList.name}</h3>
              {loadingWords ? (
                <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : listWords.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No words in this list. Add words from the Reader or Word List popover.</p>
              ) : (
                <div className="divide-y border rounded-md">
                  {listWords.map((word, i) => {
                    const wId = word.word_id || word._id || word.state_id || `w-${i}`;
                    const wText = word.word || word.normalized_form || '?';
                    const otherLists = lists.filter(l => l.list_id !== selectedList.list_id);
                    return (
                      <div key={wId} className="flex items-center justify-between px-3 py-2 text-sm group" data-testid={`fc-word-${wId}`}>
                        <span className="font-medium font-reader">{wText}</span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {otherLists.length > 0 && (
                            <>
                              <select
                                className="text-xs bg-muted/50 border rounded px-1 py-0.5 max-w-[100px]"
                                defaultValue=""
                                onChange={(e) => {
                                  if (!e.target.value) return;
                                  handleCopyWord(wId, e.target.value);
                                  e.target.value = '';
                                }}
                                title="Copy to list"
                              >
                                <option value="">Copy...</option>
                                {otherLists.map(l => <option key={l.list_id} value={l.list_id}>{l.name}</option>)}
                              </select>
                              <select
                                className="text-xs bg-muted/50 border rounded px-1 py-0.5 max-w-[100px]"
                                defaultValue=""
                                onChange={(e) => {
                                  if (!e.target.value) return;
                                  handleMoveWord(wId, e.target.value);
                                  e.target.value = '';
                                }}
                                title="Move to list"
                              >
                                <option value="">Move...</option>
                                {otherLists.map(l => <option key={l.list_id} value={l.list_id}>{l.name}</option>)}
                              </select>
                            </>
                          )}
                          <button
                            onClick={() => handleRemoveWord(wId)}
                            className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                            title="Remove from list"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Select a list to view its words
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
